import '../../components.scss';

import { RootState, useAppDispatch } from '_store';
import { getTopologyType } from '_store/application/hooks';
import { pmvL10nArgs } from '_store/application/pmvLabels';
import applicationReducer from '_store/application/reducer';
import { themeSelector } from '_store/application/selectors';
import { AppTheme, DialogType, TopologyType } from '_store/application/types';
import { useLocalization } from '@fluent/react';
import {
  Avatar,
  Box,
  Button,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useSelector } from 'react-redux';
// Import PDFs as URLs using Parcel 2.0 url: prefix
// eslint-disable-next-line import/no-unresolved
import manual5T from 'url:../../../assets/manuals/SINELEC_Manuale_EMERAS_5T_draft_v1.pdf';
// eslint-disable-next-line import/no-unresolved
import manualSATAP from 'url:../../../assets/manuals/SINELEC_Manuale_EMERAS_SATAP_draft_v0.3.pdf';

import { useRolePermissions } from '../../../hooks/useRolePermissions';
import { IconMenu, ManualIcon } from '../../icons';
import UserManualModal from '../../items/UserManualModal';
import InfoView from '../InfoView';
import MapView from '../MapView';
import styles from './styles';

const connexUrl = process.env.CONNEX_URL;
const showDashboard =
  process.env.SHOW_DASHBOARD === 'true' ||
  process.env.SHOW_DASHBOARD === 'TRUE' ||
  process.env.SHOW_DASHBOARD === '1';

const isUrbanTopology = getTopologyType() === TopologyType.URBAN;

// Select the appropriate manual based on topology type
const getUserManualPdfUrl = (): string => {
  // Fallback to environment variable if provided
  const envPdfUrl = process.env.USER_MANUAL_PDF_URL;
  if (envPdfUrl) {
    return envPdfUrl;
  }
  // Otherwise use local PDFs based on topology
  return isUrbanTopology ? manual5T : manualSATAP;
};

const menuIconsTitles = [
  { type: DialogType.HOME, label: 'menu-home', active: true },
  { type: DialogType.DENM_LIST, label: 'menu-denm', active: false },
  { type: DialogType.IVIM_LIST, label: 'menu-ivim', active: false },
  { type: DialogType.PMV_LIST, label: 'menu-pmv', active: false },
  { type: DialogType.CAM_LIST, label: 'menu-cam', active: false },
  { type: DialogType.DEVICE, label: 'menu-rsu', active: false },
  ...(isUrbanTopology ? [{ type: DialogType.SPATEM_LIST, label: 'menu-spatem', active: false }] : []),
  { type: DialogType.CONNEX, label: 'menu-connex', active: false },
  { type: DialogType.SETTINGS, label: 'menu-settings', active: false },
  { type: DialogType.DASHBOARD, label: 'menu-dashboard', active: false },
  { type: DialogType.AUDIT_LIST, label: 'menu-audit', active: false },
  // { type: DialogType.MAP, label: 'menu-map', active: false },
];

const SideMenu = () => {
  const { l10n } = useLocalization();
  const classes = styles();
  const auth = useAuth();
  const { canManageSettings, blockedActionMode, hasValidRole } = useRolePermissions();
  const profile = auth?.user?.profile;

  const logout = useCallback(() => {
    auth.signoutRedirect({
      post_logout_redirect_uri: process.env.KEYCLOAK_REDIRECT_URI,
    });
  }, [auth]);

  const [menuIcons, setMenuIcons] = useState(menuIconsTitles);
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [manualModalOpen, setManualModalOpen] = useState(false);

  const theme = useSelector(themeSelector);

  const [mapViewKey, setMapViewKey] = useState(0);

  useEffect(() => {
    // Incrementa la chiave per forzare il ri-rendering del MapView quando il tema cambia
    setMapViewKey((prevKey) => prevKey + 1);
  }, [theme]);

  // const devices = useSelector((state: RootState) => state.api.devices);
  // const denm = useSelector((state: RootState) => state.api.denm);
  // const nodes = useSelector((state: RootState) => state.api.nodes);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!hasValidRole) {
      dispatch(applicationReducer.actions.pointSelectionAborted());
      dispatch(applicationReducer.actions.dialogClosed());
      setManualModalOpen(false);
    }
  }, [hasValidRole, dispatch]);

  const dialog = useSelector((state: RootState) => state.application.activeDialog);

  const setInfoView = (text: DialogType, index: number) => {
    if (index === 0 || text === dialog?.type) {
      // Clear the point selection state when closing the dialog
      dispatch(applicationReducer.actions.pointSelectionAborted());
      dispatch(applicationReducer.actions.dialogClosed());
    } else {
      // Clear the point selection state when navigating between pages
      dispatch(applicationReducer.actions.pointSelectionAborted());
      dispatch(applicationReducer.actions.infoSelected({ type: text, id: String(index) }));
    }
  };

  useEffect(() => {
    if (dialog) {
      const isDENMRelated = [DialogType.DENM, DialogType.DENM_PATH].includes(dialog.type);

      const isIVIMRelated = [DialogType.IVIM, DialogType.IVIM_PATH].includes(dialog.type);

      const isPMVRelated = [DialogType.SINGLE_PMV, DialogType.PMV_PATH].includes(dialog.type);

      const isDEVICERelated = [DialogType.SINGLE_DEVICE, DialogType.DEVICE_PATH].includes(
        dialog.type,
      );

      const isSPATEMRelated = [DialogType.SINGLE_SPATEM].includes(dialog.type);

      const updateActive = menuIconsTitles.map((item) => ({
        ...item,
        active:
          item.type === dialog.type ||
          (isDENMRelated && item.type === DialogType.DENM_LIST) ||
          (isIVIMRelated && item.type === DialogType.IVIM_LIST) ||
          (isPMVRelated && item.type === DialogType.PMV_LIST) ||
          (isDEVICERelated && item.type === DialogType.DEVICE) ||
          (isSPATEMRelated && item.type === DialogType.SPATEM_LIST),
      }));

      setMenuIcons(updateActive);
    } else {
      setMenuIcons(menuIconsTitles);
    }
  }, [dialog]);

  const handleUserMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'user-popover' : undefined;

  return (
    <Container maxWidth={false} className={classes.container}>
      <Drawer
        className={classes.drawer}
        variant="permanent"
        classes={{
          paper: classes.drawerPaper,
        }}
        anchor="left"
      >
        <div className="Bitmap" />

        {hasValidRole ? (
          <List className={classes.listStyle}>
            {menuIcons.map((icon, index) => {
              if (!connexUrl && icon.type === DialogType.CONNEX) return null;
              if (icon.type === DialogType.DASHBOARD && !showDashboard) return null;
              if (
                icon.type === DialogType.SETTINGS &&
                !canManageSettings &&
                blockedActionMode === 'hide'
              ) {
                return null;
              }

              const isLabelLong = l10n.getString(
                icon.label,
                icon.type === DialogType.PMV_LIST ? pmvL10nArgs() : undefined,
              ).length >= 10;
              return (
                <ListItem
                  button
                  key={icon.type}
                  className={icon.active ? 'box-selected' : 'box-unselected'}
                  onClick={() =>
                    icon.type === DialogType.CONNEX
                      ? window.open(connexUrl, '_blank', 'noopener,noreferrer')
                      : setInfoView(icon.type, index)
                  }
                >
                  <ListItemIcon
                    style={{
                      justifyContent: 'center',
                    }}
                  >
                    {<IconMenu type={icon.type} active={icon.active} />}
                  </ListItemIcon>
                  <ListItemText
                    primary={l10n.getString(
                      icon.label,
                      icon.type === DialogType.PMV_LIST ? pmvL10nArgs() : undefined,
                    )}
                    className={icon.active ? 'label-selected' : 'unselected'}
                    classes={{ primary: isLabelLong && classes.menuItemTextSmall }}
                    style={{ color: theme === AppTheme.LIGHT ? 'red' : '#ffffff' }}
                  />
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Box className={classes.listStyle} aria-hidden />
        )}
        <Avatar aria-label="recipe" className={classes.avatar} onClick={handleUserMenu} />
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <div className={classes.userPopover}>
            <span>
              {`${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim() ||
                profile?.preferred_username ||
                ''}
            </span>
            {hasValidRole ? (
              <Button
                className={classes.manualButton}
                onClick={() => {
                  setManualModalOpen(true);
                  handleClose();
                }}
                startIcon={<ManualIcon />}
                style={{ color: theme === AppTheme.LIGHT ? '#005abb' : '#ececec' }}
              >
                {l10n.getString('user-manual-title')}
              </Button>
            ) : null}
            <Button
              className={classes.subHeaderStyle}
              onClick={() => logout()}
              style={{ color: theme === AppTheme.LIGHT ? '#005abb' : '#ececec' }}
            >
              {l10n.getString('user-logout')}
            </Button>
          </div>
        </Popover>
      </Drawer>

      {hasValidRole && dialog ? <InfoView /> : null}

      {hasValidRole ? (
        <MapView key={mapViewKey} />
      ) : (
        <Box className={classes.noPermissionsNotice}>
          <Alert severity="warning">
            <strong>{l10n.getString('forms-rbac-missing-permissions-title')}</strong>
            <div>{l10n.getString('forms-rbac-missing-permissions-body')}</div>
          </Alert>
        </Box>
      )}

      <UserManualModal
        open={manualModalOpen}
        onClose={() => setManualModalOpen(false)}
        pdfUrl={getUserManualPdfUrl()}
      />
    </Container>
  );
};

export default React.memo(SideMenu);
